import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import './PostpartumSection.css';

function PostpartumWarnings() {
  const { t } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [warningSigns, setWarningSigns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const warningsData = await api.getPostpartumWarningSigns();
      setWarningSigns(warningsData);
    } catch (error) {
      console.error('Error loading postpartum warnings:', error);
      setError(t('postpartum.error.load', 'Unable to load postpartum data. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="postpartum-section">
        <LoadingSpinner message={t('postpartum.loading.warnings', 'Loading warning signs information...')} />
      </div>
    );
  }

  return (
    <div className="postpartum-section">
      <div className="section-header">
        <button className="btn-back" onClick={() => navigate(`/postpartum/${userId}`)}>
          ← {t('action.back', 'Back')}
        </button>
        <h1>⚠️ {t('postpartum.title.warnings', 'Postpartum Warning Signs')}</h1>
        <nav className="postpartum-nav">
          <Link to={`/postpartum/${userId}/overview`} className="nav-link">{t('postpartum.nav.overview', 'Overview')}</Link>
          <Link to={`/postpartum/${userId}/recovery`} className="nav-link">{t('postpartum.nav.recovery', 'Recovery')}</Link>
          <Link to={`/postpartum/${userId}/nutrition`} className="nav-link">{t('postpartum.nav.nutrition', 'Nutrition')}</Link>
          <Link to={`/postpartum/${userId}/appointments`} className="nav-link">{t('postpartum.nav.appointments', 'Appointments')}</Link>
          <Link to={`/postpartum/${userId}/warnings`} className="nav-link active">{t('postpartum.nav.warnings', 'Warnings')}</Link>
        </nav>
      </div>

      <div className="section-content">
        {error && <div className="postpartum-error">{error}</div>}
        {warningSigns && (
          <>
            {warningSigns.redFlags && warningSigns.redFlags.length > 0 && (
              <section className="data-section red-flags-section">
                <h2>🚨 {t('postpartum.red.flags', 'Red Flags - Call Doctor Immediately')}</h2>
                <div className="nutrient-foods-grid">
                  {warningSigns.redFlags.map((category, i) => (
                    <div key={i} className="nutrient-category nutrient-category-warning" style={{backgroundColor: '#fff5f5'}}>
                      <h3>⚠️ {category.category}</h3>
                      {category.signs && category.signs.length > 0 && (
                        <div style={{marginBottom: '15px'}}>
                          <strong style={{display: 'block', marginBottom: '8px', color: '#c62828'}}>Warning Signs:</strong>
                          <ul>
                            {category.signs.map((sign, j) => (
                              <li key={j} style={{marginBottom: '5px'}}>{sign}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {category.action && (
                        <p style={{fontSize: '0.9rem', color: '#c62828', fontWeight: 'bold', marginTop: '8px'}}>
                          ⚡ {category.action}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {warningSigns.whenToCall && (
              <section className="data-section when-to-call-section">
                <h2>📞 {t('postpartum.when.to.call', 'When to Call Your Doctor')}</h2>
                <div className="nutrient-foods-grid">
                  <div className="nutrient-category">
                    {warningSigns.whenToCall.immediately && warningSigns.whenToCall.immediately.length > 0 && (
                      <div style={{marginBottom: '20px'}}>
                        <h3 style={{color: '#c62828', marginBottom: '10px'}}>🚨 {t('postpartum.call.immediately', 'Call Immediately')}</h3>
                        <ul>
                          {warningSigns.whenToCall.immediately.map((item, i) => (
                            <li key={i} style={{color: '#c62828', marginBottom: '5px'}}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {warningSigns.whenToCall.within24Hours && warningSigns.whenToCall.within24Hours.length > 0 && (
                      <div style={{marginBottom: '20px'}}>
                        <h3 style={{color: '#f57c00', marginBottom: '10px'}}>⏰ {t('postpartum.call.24hours', 'Call Within 24 Hours')}</h3>
                        <ul>
                          {warningSigns.whenToCall.within24Hours.map((item, i) => (
                            <li key={i} style={{color: '#f57c00', marginBottom: '5px'}}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {warningSigns.whenToCall.routine && warningSigns.whenToCall.routine.length > 0 && (
                      <div>
                        <h3 style={{color: '#1976d2', marginBottom: '10px'}}>📋 {t('postpartum.routine.questions', 'Routine Questions')}</h3>
                        <ul>
                          {warningSigns.whenToCall.routine.map((item, i) => (
                            <li key={i} style={{color: '#1976d2', marginBottom: '5px'}}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PostpartumWarnings;

